import React, { useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { lmsAdmin, readFailureText } from '../api'
import { relativeTime } from '../format'
import { FleetPage, FleetSection, Notice } from '../../../shared/sso-workspace/fleet-page'
import { LocalTime } from '../../../shared/date/local-time-view'
import { utcText } from '../../../shared/date/local-time'
import '../lms-admin.scss'
import { LmsTrail, lmsTrail } from '../lms-trail'

// Session Detail (docs/08 §20, design edxr-session-detail-v1.0.html).
// Drill-down from Active Sessions: state timeline (row timestamps +
// audit entries), writeback result, device/claim info. The artifact's
// per-attempt writeback retry log (Retrying / Giving up, force-retry)
// has no v1 backing — writeback happens once on completion and v1
// stores only its result (deviations D-047).
//
// It is a standard admin page (owner feedback 2026-10-20: a fixed-width card
// with an X did not follow the rest of the admin): FleetPage's pane, the title
// with the session's state, a back link to Sessions, and the sections full width.
//
// "The result for your LMS" is the session's writeback: the lesson status, score
// and time the service stored when the learner finished (session_status,
// session_score, session_duration_sec on the session this page already reads),
// and when that was recorded, which is the session's completion in its timeline.
// Whether the LMS has it is the service's written_back_at (review M1): a time
// (title "Sent <time>"), null (the service has not been told the LMS took it:
// "Not sent to your LMS yet"), or no such key at all (a service older than the
// field, and then the page claims nothing: "The result for your LMS"). The page
// says "Recorded" for the completion, never "Received".

// The session's states (rapid-api-lms domain/session/agg.go). A launch is
// linked in one step since Phase F, but one from before it may still wait in
// pending_link_confirmation.
const STATE_PILLS = {
  unlinked: { tone: 'info', label: 'Waiting for account link' },
  pending_link_confirmation: { tone: 'info', label: 'Waiting for account link' },
  ready: { tone: 'info', label: 'Ready' },
  claimed: { tone: 'warn', label: 'Claimed' },
  in_progress: { tone: 'ok', label: 'In progress' },
  completed: { tone: 'ok', label: 'Completed' },
  failed: { tone: 'bad', label: 'Failed' },
  rejected: { tone: 'bad', label: 'Rejected' },
  expired: { tone: null, label: 'Expired' }
}

const humanize = state =>
  (state || '')
    .replace(/[._]/g, ' ')
    .replace(/^\w/, c => c.toUpperCase())

const fmtDuration = seconds => {
  if (seconds === undefined || seconds === null) return '—'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m ? `${m}m ${s}s` : `${s}s`
}

// The lesson status the LMS was sent, in words (passed -> Passed, not attempted
// -> Not attempted).
const lessonStatus = status => (status ? humanize(status) : '—')

// The completion in the session's timeline: the last one, which is the
// session's own (the service adds it after the events of the audit trail).
const completedAt = timeline => {
  const done = [...timeline].reverse().find(entry => entry.state === 'completed')
  return done ? done.at : null
}

// Whether the session has anything to send: a lesson status, a score or a time.
const hasResult = session =>
  Boolean(session.session_status) ||
  (session.session_score !== null && session.session_score !== undefined) ||
  (session.session_duration_sec !== null && session.session_duration_sec !== undefined)

// A session still to be linked or played has not sent its result; one that ended
// without one never will.
const PENDING = ['unlinked', 'pending_link_confirmation', 'ready', 'claimed', 'in_progress']

// Whether the service said anything about the LMS having the result: its
// written_back_at key, a time or null. An older service sends no such key.
const saysWrittenBack = session => Object.prototype.hasOwnProperty.call(session, 'written_back_at')

// A SCORM learner's newest completed result that the LMS has not taken goes with
// the learner's next launch of the course (the service's resync). One that ended
// incomplete is not a result and is never carried (domain/session/results-pg.go).
const carriedToNextLaunch = session => session.lms_user_id_kind === 'scorm' && session.state === 'completed' && session.session_status !== 'incomplete'

// What the section is titled, and what it says under the title, for each of the
// three things the service can say.
const writebackWords = session => {
  const sentAt = session.written_back_at
  if (sentAt) {
    return {
      title: (
        <>
          Sent <LocalTime value={sentAt} seconds />
        </>
      ),
      lede: 'The lesson status, score and time your LMS was given.'
    }
  }
  if (saysWrittenBack(session) && hasResult(session)) {
    let lede = null
    if (carriedToNextLaunch(session)) lede = "The learner's next launch of this course sends it, unless a newer result replaces it."
    else if (session.lms_user_id_kind === 'lti13') {
      lede = (
        <>
          A grade that could not be sent is listed on <Link to={`/org/lms/connections/${session.connection_id}`}>the connection's page</Link>, where it can be sent again.
        </>
      )
    }
    return { title: 'Not sent to your LMS yet', lede }
  }
  return { title: 'The result for your LMS', lede: "What the learner's session recorded when they finished the course." }
}

const WritebackSection = ({ session, timeline }) => {
  const recorded = completedAt(timeline)
  const words = writebackWords(session)
  return (
    <FleetSection id="session-writeback" title={words.title} lede={words.lede}>
      {hasResult(session) ? (
        <dl className="sw-dl">
          <dt>Lesson status</dt>
          <dd data-testid="writeback-status">{lessonStatus(session.session_status)}</dd>
          <dt>Score</dt>
          {/* session_score is SCORM raw scale (0-100), not a fraction. */}
          <dd data-testid="writeback-score">{session.session_score === null || session.session_score === undefined ? '—' : `${session.session_score}%`}</dd>
          <dt>Time spent</dt>
          <dd data-testid="writeback-time">{fmtDuration(session.session_duration_sec)}</dd>
          <dt>Recorded</dt>
          <dd data-testid="writeback-recorded">{recorded ? <LocalTime value={recorded} seconds /> : '—'}</dd>
        </dl>
      ) : (
        <p className="sw-hint m-0">
          {PENDING.includes(session.state) ? 'Nothing has been sent to your LMS yet.' : 'Nothing was sent to your LMS for this session.'}
        </p>
      )}
    </FleetSection>
  )
}

const eventCount = n => `${n} ${n === 1 ? 'event' : 'events'}`

// Where the back link goes (review L2): the list the person opened the session
// from (an opener passes its address, query included, as location.state.from),
// else Active sessions, the list the page is a drill-down of. Only this
// section's lists are taken, by name, never an address from anywhere else.
const BACK_TARGETS = {
  '/org/lms/activity': 'Activity',
  '/org/lms/sessions': 'Active sessions'
}
const DEFAULT_BACK = { to: '/org/lms/sessions', label: 'Active sessions' }

const backTarget = from => {
  if (typeof from !== 'string' || !from.startsWith('/') || from.startsWith('//')) return DEFAULT_BACK
  const label = BACK_TARGETS[from.split(/[?#]/)[0]]
  return label ? { to: from, label } : DEFAULT_BACK
}

export const ViewSessionDetail = () => {
  const { sessionRef } = useParams()
  const location = useLocation()
  const back = backTarget(location.state && location.state.from)

  const [session, updateSession] = useState(null)
  const [error, updateError] = useState(null)

  useEffect(() => {
    lmsAdmin.getSession(sessionRef).then(updateSession).catch(updateError)
  }, [sessionRef])

  const pill = session && (STATE_PILLS[session.state] || { tone: null, label: humanize(session.state) })
  const created = session && relativeTime(session.created_at)
  const timeline = (session && session.state_timeline) || []

  const title = (
    <>
      {session && session.course ? session.course.name : 'Session'}
      {pill && (
        <span className={['sw-pill', pill.tone, 'ms-2', 'align-middle'].filter(Boolean).join(' ')} data-testid="session-state">
          {pill.label}
        </span>
      )}
    </>
  )
  const purpose = session ? (
    <>
      {session.edxr_user_email || 'Not yet linked'}
      {created && (
        <>
          {' · '}
          <span title={utcText(session.created_at)}>launched {created.text}</span>
        </>
      )}
    </>
  ) : null

  return (
    <FleetPage testId="session-detail" title={title} purpose={purpose}>
      <LmsTrail
        trail={lmsTrail(
          { url: '/org/lms/sessions', text: 'Sessions' },
          { url: `/org/lms/sessions/${sessionRef}`, text: session && session.course ? session.course.name : 'Session' }
        )}
      />
      <p className="mb-2">
        <Link to={back.to} data-testid="session-back">
          ← {back.label}
        </Link>
      </p>

      {error && (
        <Notice tone="bad" role="alert">
          {error.code === 'NOT_FOUND' ? 'Session not found.' : readFailureText('The session', error)}
        </Notice>
      )}
      {!session && !error && (
        <p className="sw-hint" role="status">
          Loading…
        </p>
      )}

      {session && (
        <>
          <WritebackSection session={session} timeline={timeline} />

          <FleetSection id="session-details" title="Details">
            <dl className="sw-dl">
              <dt>Session ref</dt>
              <dd className="sw-mono">{session.ref}</dd>
              <dt>LMS user</dt>
              <dd className="sw-mono">{session.lms_user_id || '—'}</dd>
              <dt>Connection</dt>
              <dd>#{session.connection_id}</dd>
              <dt>Device</dt>
              <dd>{session.device_label || '—'}</dd>
              <dt>Claimed via</dt>
              <dd>
                {session.claim_method || '—'}
                {session.claim_source && <span className="sw-hint ms-2">{session.claim_source}</span>}
              </dd>
            </dl>
          </FleetSection>

          <FleetSection id="session-timeline" title="State timeline" lede={eventCount(timeline.length)}>
            <ol className="lms-timeline">
              {timeline.map((entry, index) => (
                <li key={`${entry.state}-${index}`} data-testid={`timeline-${index}`}>
                  <span className="lms-timeline-state">{humanize(entry.state)}</span>
                  <LocalTime value={entry.at} seconds />
                </li>
              ))}
            </ol>
          </FleetSection>
        </>
      )}
    </FleetPage>
  )
}
